import type { Href } from "expo-router";
import { Link, useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  Text,
  View,
} from "react-native";

import {
  acceptInvite,
  clearPendingInviteToken,
  getPendingInviteToken,
  portalHrefAfterAccept,
  setPendingInviteToken,
} from "../../../lib/invites";
import { supabase } from "../../../lib/supabase";
import { Button, Card, Input, Section } from "../../../components/ui";

function firstParam(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

export default function JudgeSignupScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ email?: string | string[]; token?: string | string[] }>();
  const paramEmail = firstParam(params.email);
  const paramToken = firstParam(params.token);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmMsg, setConfirmMsg] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    void (async () => {
      const stored = await getPendingInviteToken();
      if (!mounted) return;
      const t = paramToken ?? stored ?? null;
      setInviteToken(t);
      if (paramEmail) setEmail(paramEmail.trim().toLowerCase());
    })();
  }, [paramEmail, paramToken]);

  const emailLocked = !!paramEmail;

  async function onSubmit() {
    setError(null);
    setConfirmMsg(null);
    if (!fullName.trim() || !email.trim() || !password) {
      setError("Please complete all required fields.");
      return;
    }

    setBusy(true);
    try {
      const { data, error: signErr } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: { full_name: fullName.trim() },
        },
      });
      if (signErr) {
        setError(signErr.message);
        return;
      }
      if (!data.session) {
        if (inviteToken) await setPendingInviteToken(inviteToken);
        setConfirmMsg("Check your email to confirm your account, then sign in. Your invite will finish when you sign in.");
        return;
      }

      if (inviteToken) {
        try {
          const result = await acceptInvite(inviteToken);
          await clearPendingInviteToken();
          router.replace(portalHrefAfterAccept(result));
          return;
        } catch (e) {
          setError(e instanceof Error ? e.message : "Invite could not be applied.");
          return;
        }
      }
      router.replace("/judge" as Href);
    } finally {
      setBusy(false);
    }
  }

  return (
    <KeyboardAvoidingView className="flex-1 bg-forest" behavior={Platform.OS === "ios" ? "padding" : undefined}>
      <View className="flex-1 items-center justify-center px-5 py-8">
        <Card variant="light" padding="lg">
          <Section overline="Create Judge Account" title="Judge sign up">
          <Text className="mb-4 text-center text-sm text-primary/70">
            Create a password for the email your competition host invited.
          </Text>

          <Input label="Full name" value={fullName} onChangeText={setFullName} />

          <View className="mt-3">
            <Input label="Email" keyboardType="email-address" value={email} onChangeText={setEmail} helper={emailLocked ? "Locked to invite email" : undefined} />
          </View>

          <View className="mt-3">
            <Input label="Password" secureTextEntry value={password} onChangeText={setPassword} />
          </View>

          {error ? <Text className="mb-3 text-sm text-red-600">{error}</Text> : null}
          {confirmMsg ? <Text className="mb-3 text-sm text-emerald-700">{confirmMsg}</Text> : null}

          <View className="mt-3">
            <Button variant="primary" loading={busy} onPress={() => void onSubmit()}>
              Create judge account
            </Button>
          </View>

          <Link href="/login" asChild>
            <Pressable>
              <Text className="text-center text-sm text-accent">Already have an account? Sign in</Text>
            </Pressable>
          </Link>
          </Section>
        </Card>
      </View>
    </KeyboardAvoidingView>
  );
}
